import React, { useState, useRef, useEffect } from 'react';
import './FilterDropdown.css';

/**
 * Custom single-select dropdown (button + menu).
 * Same pattern as Alerts filters — avoids native <select> OS styling.
 *
 * options: [{ value|id, label }, ...]
 */
function FilterDropdown({
  options = [],
  value,
  onChange,
  title = '',
  id,
  className = '',
  menuPlacement = 'down',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const optionValue = (opt) => (opt.value !== undefined ? opt.value : opt.id);
  const selectedOption = options.find((opt) => optionValue(opt) === value);
  const triggerLabel = selectedOption?.label ?? String(value ?? 'Select…');

  const handlePick = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div
      className={`filter-dropdown ${menuPlacement === 'up' ? 'filter-dropdown--up' : ''} ${className}`.trim()}
      ref={rootRef}
      id={id}
    >
      <button
        type="button"
        className={`filter-dropdown-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
      >
        <span className="filter-dropdown-label">{triggerLabel}</span>
        <span className="filter-dropdown-chevron" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div className="filter-dropdown-menu" role="listbox">
          {options.map((opt) => {
            const val = optionValue(opt);
            const isSelected = val === value;
            return (
              <button
                key={String(val)}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`filter-dropdown-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handlePick(val)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
